import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Snackbar,
  Alert,
  IconButton,
  Drawer,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import CircularProgress from '@mui/material/CircularProgress';
import MenuIcon from '@mui/icons-material/Menu';
import InfoIcon from '@mui/icons-material/Info'; // for the right sidebar toggle

import Sidebar from './Sidebar';
import ChatArea from './ChatArea';
import TickerSidebar from './TickerSidebar';
import api from '../services/api'; // Import the Axios instance

interface Message {
  sender: 'user' | 'bot';
  content: string;
}

interface ChatItem {
  id: string;
  title: string;
  created: string;
}

const Chat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isFetchingChats, setIsFetchingChats] = useState(false);
  const [isFetchingMessages, setIsFetchingMessages] = useState(false);
  const [chats, setChats] = useState<ChatItem[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // For responsive layout
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Drawer toggles for sidebars
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);

  useEffect(() => {
    fetchChats();
  }, []);

  useEffect(() => {
    if (selectedChatId) {
      fetchChatMessages(selectedChatId);
    }
  }, [selectedChatId]);

  const fetchChats = async () => {
    setIsFetchingChats(true);
    try {
      const response = await api.get<ChatItem[]>('/chat');
      setChats(response.data);
    } catch (error) {
      console.error('Error fetching chats:', error);
      setError('Failed to fetch chats. Please try again.');
    } finally {
      setIsFetchingChats(false);
    }
  };

  const fetchChatMessages = async (chatId: string) => {
    setIsFetchingMessages(true);
    try {
      const response = await api.get(`/chat-message`, {
        params: { chat_id: chatId },
      });
      const data = response.data;
      const fetchedMessages: Message[] = data.map((msg: any) => ({
        sender: msg.is_human_message ? 'user' : 'bot',
        content: msg.content,
      }));

      setMessages(fetchedMessages);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError('Failed to fetch messages. Please try again.');
    } finally {
      setIsFetchingMessages(false);
    }
  };

  const handleCreateChat = async (): Promise<ChatItem | null> => {
    setIsCreatingChat(true);
    try {
      const response = await api.post<ChatItem>('/chat', {});
      const newChat = response.data;
      setChats((prevChats) => [newChat, ...prevChats]);
      setSelectedChatId(newChat.id);
      setMessages([]);
      return newChat;
    } catch (error) {
      console.error('Error creating chat:', error);
      setError('Failed to create chat. Please try again.');
      return null;
    } finally {
      setIsCreatingChat(false);
    }
  };

  const handleSelectChat = (chatId: string) => {
    if (isSendingMessage || isFetchingMessages) return; // Prevent selecting another chat while loading
    setSelectedChatId(chatId);
    setMessages([]);
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    let currentChatId = selectedChatId;
    if (!currentChatId) {
      const newChat = await handleCreateChat();
      if (!newChat) {
        // If creation failed, stop here
        return;
      }
      currentChatId = newChat.id;
    }

    const userMessage: Message = { sender: 'user', content: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsSendingMessage(true);

    try {
      await fetchChatResponse(currentChatId!, userMessage.content);
    } catch (error) {
      const errorMessage: Message = {
        sender: 'bot',
        content: 'An error occurred. Please try again.',
      };
      setMessages((prev) => [...prev, errorMessage]);
      console.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const fetchChatResponse = async (chatId: string, input: string): Promise<void> => {
    if (!chatId) {
      console.error('No chat selected');
      setError('No chat selected.');
      return;
    }

    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch('https://api.shmoney.ai/agent/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
          body: JSON.stringify({ chat_id: chatId, query: input }),
        });

        if (!response.ok || !response.body) {
          reject('Network response was not ok');
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let done = false;
        let result = '';

        // Initialize bot message with empty content
        setMessages((prev) => [...prev, { sender: 'bot', content: '' }]);

        while (!done) {
          const { value, done: doneReading } = await reader.read();
          done = doneReading;
          const chunkValue = decoder.decode(value || new Uint8Array(), {
            stream: !done,
          });
          result += chunkValue;

          setMessages((prev) => {
            const updatedMessages = [...prev];
            const lastIndex = updatedMessages.length - 1;
            updatedMessages[lastIndex] = {
              sender: 'bot',
              content: result,
            };
            return updatedMessages;
          });
        }

        resolve();
      } catch (error) {
        reject(error);
      }
    });
  };

  return (
      <Container
          maxWidth="xl"
          sx={{ mt: 4, height: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        {/*
        Here are two optional icon buttons (or any other controls)
        to toggle sidebars on mobile.
        You might place these inside ChatArea’s header
        or a small toolbar above everything, etc.
      */}
        {isMobile && (
            <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
            >
              {/* Left sidebar toggle */}
              <IconButton color="primary" onClick={() => setLeftOpen(true)}>
                <MenuIcon />
              </IconButton>

              {/* Right sidebar toggle */}
              <IconButton color="primary" onClick={() => setRightOpen(true)}>
                <InfoIcon />
              </IconButton>
            </Box>
        )}

        <Grid container spacing={2} sx={{ flexGrow: 1, height: '100%' }}>
          {/* Left Sidebar - hidden on mobile (replaced by Drawer) */}
          {!isMobile && (
              <Grid item xs={12} md={3}>
                <Sidebar
                    chats={chats}
                    isFetchingChats={isFetchingChats}
                    isCreatingChat={isCreatingChat}
                    selectedChatId={selectedChatId}
                    handleCreateChat={handleCreateChat}
                    handleSelectChat={handleSelectChat}
                    isSendingMessage={isSendingMessage}
                    isFetchingMessages={isFetchingMessages}
                />
              </Grid>
          )}

          {/* Chat Area */}
          <Grid
              item
              xs={12}
              md={6}
              sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            <ChatArea
                messages={messages}
                input={input}
                setInput={setInput}
                handleSendMessage={handleSendMessage}
                isSendingMessage={isSendingMessage}
                isFetchingMessages={isFetchingMessages}
                selectedChatId={selectedChatId}
            />
          </Grid>

          {/* Right Sidebar (TickerSidebar) - hidden on mobile (replaced by Drawer) */}
          {!isMobile && (
              <Grid item xs={12} md={3}>
                <TickerSidebar />
              </Grid>
          )}
        </Grid>

        {/* Drawer for Left Sidebar */}
        <Drawer anchor="left" open={leftOpen} onClose={() => setLeftOpen(false)}>
          <Box sx={{ width: 250, p: 2 }}>
            <Sidebar
                chats={chats}
                isFetchingChats={isFetchingChats}
                isCreatingChat={isCreatingChat}
                selectedChatId={selectedChatId}
                handleCreateChat={handleCreateChat}
                handleSelectChat={handleSelectChat}
                isSendingMessage={isSendingMessage}
                isFetchingMessages={isFetchingMessages}
            />
          </Box>
        </Drawer>

        {/* Drawer for Right Sidebar (TickerSidebar) */}
        <Drawer anchor="right" open={rightOpen} onClose={() => setRightOpen(false)}>
          <Box sx={{ width: 250, p: 2 }}>
            <TickerSidebar />
          </Box>
        </Drawer>

        {/* Snackbar for Error Messages */}
        <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </Container>
  );
};

export default Chat;
